// @flow
import * as React from 'react';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { withLeaflet } from 'react-leaflet';
import type { VisualisedFiber } from 'esm-web-pro/containers/pages/network/objects/cable/FiberList';
import { highlightFiberCircuit } from 'esm-web-pro/containers/pages/network/highlight';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import type { Map } from 'esm-web-pro/containers/map/types';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import useHighlightFiber from 'esm-web-pro/containers/pages/network/objects/fiber/useHighlightFiber';
import UseHighlightPoint from 'esm-web-pro/hooks/useHighlightPoint';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { isEmpty } from 'lodash';
import type { UUID } from 'esm-web-pro/types/common';


type Props = {
    point?: Object,
    initialFiber: ?string,
    secondFiber?: string, // Either the secondFiber prop or the secondPoint14 prop should be set
    secondPoint14?: { uuid: UUID, resource: string },
    editor: BaseEditor,
    map: Map,

    getFiberCircuitAndPoints: Function,
    leaflet: LeafletContext,
};

const MapFiberConnection = (props: Props) => {
    const {
        point, leaflet: { map }, initialFiber, secondFiber, getFiberCircuitAndPoints,
        editor, map: networkMap, secondPoint14,
    } = props;

    const [highlightFiber] = useHighlightFiber();
    const [secondFiberCallbacks, setSecondFiberCallbacks] = React.useState([]);

    const setFirstFiberStyle = React.useCallback(() => {
        if (initialFiber) {
            getFiberCircuitAndPoints(initialFiber, networkMap).then((vFiber: VisualisedFiber) => {
                editor.unregisterAllLoadingCallback();
                const [callbacks, circuitPath] = highlightFiberCircuit(
                    vFiber.path, vFiber, map, highlightFiber,
                );
                editor.registerMultipleLoadingCallbacks(callbacks);
                map.highlightedGeometry = {
                    ...map.highlightedGeometry, bounds: circuitPath,
                };
                editor.reloadFeatures();
            });
        } else if (point) {
            const pointCoords = point.coordinates.coordinates;
            let number = point.sequence;
            if (point.type === 'pop' && point.alias) {
                number = point.alias;
            }
            editor.registerLoadingCallback(UseHighlightPoint(pointCoords, number));
        }
    }, [initialFiber]);

    const setSecondFiberStyle = React.useCallback(() => {
        if (!secondFiber) {
            secondFiberCallbacks.forEach((callback) => editor.unregisterLoadingCallback(callback));
            editor.reloadFeatures();
            return;
        }

        getFiberCircuitAndPoints(secondFiber, networkMap).then((vFiber: VisualisedFiber) => {
            secondFiberCallbacks.forEach((callback) => editor.unregisterLoadingCallback(callback));
            const [callbacks, circuitPath] = highlightFiberCircuit(
                vFiber.path, vFiber, map, highlightFiber,
            );
            editor.registerMultipleLoadingCallbacks(callbacks);
            map.highlightedGeometry = {
                ...map.highlightedGeometry, bounds: circuitPath,
            };
            editor.reloadFeatures();
            setSecondFiberCallbacks(callbacks);
        });
    }, [secondFiber]);


    const setSecondPoint14Style = React.useCallback(() => {
        if (!secondPoint14) {
            secondFiberCallbacks.forEach((callback) => editor.unregisterLoadingCallback(callback));
            editor.reloadFeatures();
            return;
        }

        getFiberCircuitAndPoints(secondPoint14.uuid, networkMap, secondPoint14.resource)
            .then((vFiber: VisualisedFiber) => {
                secondFiberCallbacks.forEach((callback) => editor.unregisterLoadingCallback(callback));
                if (!isEmpty(vFiber)) {
                    const [callbacks, circuitPath] = highlightFiberCircuit(
                        vFiber.path, vFiber, map, highlightFiber,
                    );
                    editor.registerMultipleLoadingCallbacks(callbacks);
                    map.highlightedGeometry = {
                        ...map.highlightedGeometry, bounds: circuitPath,
                    };
                    setSecondFiberCallbacks(callbacks);
                }
                editor.reloadFeatures();
            });
    }, [secondPoint14]);


    React.useEffect(() => {
        editor.unregisterAllLoadingCallback();
        return () => {
            editor.unregisterAllLoadingCallback();
        };
    }, []);

    React.useEffect(() => {
        setFirstFiberStyle();
    }, [setFirstFiberStyle]);

    React.useEffect(() => {
        setSecondFiberStyle();
    }, [setSecondFiberStyle]);

    React.useEffect(() => {
        setSecondPoint14Style();
    }, [setSecondPoint14Style]);

    return null;
};

MapFiberConnection.defaultProps = {
    point: null,
};

export const circuitCallback = (apiClient: ApiClient) => ({
    getFiberCircuitAndPoints: {
        send: (uuid: string, map: Map, resource: string = 'fiber') => {
            if (map.is_draft) {
                return apiClient.resources[resource].circuit(uuid, { is_draft: map.uuid });
            }
            return apiClient.resources[resource].circuit(uuid);
        },
    },
});

export default withLeaflet(connectAPI(circuitCallback)(MapFiberConnection));
